/**
 * Partnr 2 panel state, shared by the panel (S3), the canvas ghost layer (S4)
 * and the document surface (S5). This is a seam file: S3 owns it and fills in
 * behavior, but the exported names and types stay stable because S4 and S5
 * import them while S3 is still being built.
 *
 * The panel writes `liveMessage` from usePartnrChat's liveAssistantMessage;
 * the ghost layer reads it to draw cards while a tool's input streams in.
 * `rightInset` is the width the open panel covers on desktop, so placement
 * and viewport helpers keep new items in the visible part of the canvas.
 */
import { create } from 'zustand';
import type { PartnrUIMessage } from '@/lib/partnr/types';

export const PARTNR_PANEL_MIN_WIDTH = 340;
export const PARTNR_PANEL_MAX_WIDTH = 640;
export const PARTNR_PANEL_DEFAULT_WIDTH = 400;

const widthKey = (userId: string) => `partnr:panel:width:${userId}`;

export function clampPanelWidth(width: number): number {
  if (!Number.isFinite(width)) return PARTNR_PANEL_DEFAULT_WIDTH;
  return Math.round(Math.min(PARTNR_PANEL_MAX_WIDTH, Math.max(PARTNR_PANEL_MIN_WIDTH, width)));
}

/** The panel width this user last chose, or the default. */
export function loadPanelWidth(userId: string | null | undefined): number {
  if (!userId) return PARTNR_PANEL_DEFAULT_WIDTH;
  try {
    const saved = Number(localStorage.getItem(widthKey(userId)));
    return saved ? clampPanelWidth(saved) : PARTNR_PANEL_DEFAULT_WIDTH;
  } catch {
    return PARTNR_PANEL_DEFAULT_WIDTH;
  }
}

function savePanelWidth(userId: string | null | undefined, width: number): void {
  if (!userId) return;
  try {
    localStorage.setItem(widthKey(userId), String(width));
  } catch {
    // Private mode or full storage: the width just won't stick
  }
}

/** Search results the bottom bar hands to Partnr ("ask about these"). */
export interface PartnrSearchHandoff {
  originalQuery: string;
  resultCount: number;
  resultItemIds: string[];
}

export interface PartnrPanelOpenOptions {
  /** Open this saved chat instead of the active one. */
  chatId?: string;
  /** Start a fresh chat even if one is active. */
  newChat?: boolean;
  /** Send this message once the chat is ready. */
  message?: string;
  /** Items to reference, shown as chips in the composer. */
  selectionIds?: string[];
  searchContext?: PartnrSearchHandoff;
}

/** What `open()` asked the panel to do, taken once by the panel. */
export interface PartnrPanelPending {
  message: string | null;
  selectionIds: string[];
  searchContext: PartnrSearchHandoff | null;
}

export interface PartnrPanelState {
  isOpen: boolean;
  width: number;
  /** partnr_chats.id shown in the panel; null means "start a new chat on send". */
  activeChatId: string | null;
  pendingMessage: string | null;
  pendingSelectionIds: string[];
  pendingSearchContext: PartnrSearchHandoff | null;
  /** The streaming assistant message, or null when nothing is streaming. */
  liveMessage: PartnrUIMessage | null;
  /** Pixels of canvas the open panel covers on the right (0 when closed or a bottom sheet). */
  rightInset: number;
  /** The past chats popover in the panel header. */
  historyOpen: boolean;
  /** Bumped when an entry point wants the composer focused; the panel watches it. */
  composerFocusRequest: number;
  /** Item ids chipped in the composer right now, written by the panel's chat. */
  composerChipIds: string[];
  /** Canvas selection the panel last took (pill or Cmd+J); chips clear on send, these don't. */
  takenSelectionIds: string[];

  open: (options?: PartnrPanelOpenOptions) => void;
  close: () => void;
  toggle: () => void;
  newChat: () => void;
  openChat: (chatId: string) => void;
  setActiveChatId: (chatId: string | null) => void;
  setWidth: (width: number, userId?: string | null) => void;
  setLiveMessage: (message: PartnrUIMessage | null) => void;
  setRightInset: (px: number) => void;
  /** Returns and clears what `open()` queued, so it is acted on exactly once. */
  takePending: () => PartnrPanelPending;
  setHistoryOpen: (open: boolean) => void;
  /** Opens the panel with its past chats popover showing (the bottom bar's history button). */
  openHistory: () => void;
  requestComposerFocus: () => void;
  setComposerChipIds: (ids: string[]) => void;
  /** Loads the width this user last chose. */
  hydrateWidth: (userId: string | null | undefined) => void;
}

export const usePartnrPanelStore = create<PartnrPanelState>((set, get) => ({
  isOpen: false,
  width: PARTNR_PANEL_DEFAULT_WIDTH,
  activeChatId: null,
  pendingMessage: null,
  pendingSelectionIds: [],
  pendingSearchContext: null,
  liveMessage: null,
  rightInset: 0,
  historyOpen: false,
  composerFocusRequest: 0,
  composerChipIds: [],
  takenSelectionIds: [],

  open: (options = {}) =>
    set((state) => ({
      isOpen: true,
      activeChatId: options.newChat ? null : options.chatId ?? state.activeChatId,
      pendingMessage: options.message?.trim() ? options.message : null,
      pendingSelectionIds: options.selectionIds ?? [],
      pendingSearchContext: options.searchContext ?? null,
      composerFocusRequest: state.composerFocusRequest + 1,
      ...(options.selectionIds ? { takenSelectionIds: options.selectionIds } : {}),
    })),

  close: () => set({ isOpen: false, rightInset: 0, historyOpen: false }),

  toggle: () => (get().isOpen ? get().close() : get().open()),

  newChat: () => set({ activeChatId: null, liveMessage: null }),

  openChat: (chatId) => set({ isOpen: true, activeChatId: chatId, liveMessage: null }),

  setActiveChatId: (chatId) => set({ activeChatId: chatId }),

  setWidth: (width, userId) => {
    const clamped = clampPanelWidth(width);
    savePanelWidth(userId, clamped);
    set({ width: clamped });
  },

  setLiveMessage: (message) => set({ liveMessage: message }),

  setRightInset: (px) => set({ rightInset: Math.max(0, Math.round(px)) }),

  takePending: () => {
    const { pendingMessage, pendingSelectionIds, pendingSearchContext } = get();
    set({ pendingMessage: null, pendingSelectionIds: [], pendingSearchContext: null });
    return { message: pendingMessage, selectionIds: pendingSelectionIds, searchContext: pendingSearchContext };
  },

  setHistoryOpen: (open) => set({ historyOpen: open }),

  openHistory: () => set({ isOpen: true, historyOpen: true }),

  requestComposerFocus: () => set((state) => ({ composerFocusRequest: state.composerFocusRequest + 1 })),

  setComposerChipIds: (ids) => set({ composerChipIds: ids }),

  hydrateWidth: (userId) => set({ width: loadPanelWidth(userId) }),
}));
